import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './auth/AuthContext';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { ActivatePage, ForgotPasswordPage, LoginPage, ResetPasswordPage } from './pages/AuthPages';
import { CurriculumPage, ImportExportPage, TechniqueEditPage } from './pages/CurriculumPages';
import { OverviewPage } from './pages/OverviewPage';
import { CertificatePage, PromotionsPage } from './pages/PromotionPages';
import { StaffPage } from './pages/PeoplePages';
import { ApprovalsPage, AuditPage, ReportsPage } from './pages/ReportPages';
import { SettingsPage } from './pages/SettingsPage';
import { StudentDetailPage } from './pages/StudentDetailPage';
import { StudentsPage } from './pages/StudentsPage';
import { VideosPage } from './pages/VideosPage';

function RequireStaff({
  ownerOnly = false,
  children,
}: {
  ownerOnly?: boolean;
  children: ReactNode;
}) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user || (user.role !== 'instructor' && user.role !== 'owner'))
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (ownerOnly && user.role !== 'owner') return <Navigate to="/students" replace />;
  return <>{children}</>;
}

const owner = (el: ReactNode) => <RequireStaff ownerOnly>{el}</RequireStaff>;

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        element={
          <RequireStaff>
            <Layout />
          </RequireStaff>
        }
      >
        <Route index element={owner(<OverviewPage />)} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="students/:id" element={<StudentDetailPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="promotions" element={<PromotionsPage />} />
        <Route path="certificates/:id" element={<CertificatePage />} />
        {/* Invitations now live on the Students and Instructors pages. */}
        <Route path="invitations" element={<Navigate to="/students" replace />} />
        <Route path="staff" element={owner(<StaffPage />)} />
        <Route path="curriculum" element={owner(<CurriculumPage />)} />
        <Route path="curriculum/:id" element={owner(<TechniqueEditPage />)} />
        {/* Categories are managed on the Curriculum page. */}
        <Route path="categories" element={<Navigate to="/curriculum" replace />} />
        <Route path="import-export" element={owner(<ImportExportPage />)} />
        <Route path="videos" element={owner(<VideosPage />)} />
        <Route path="reports" element={owner(<ReportsPage />)} />
        <Route path="audit" element={owner(<AuditPage />)} />
        <Route path="settings" element={owner(<SettingsPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
